import React, { useEffect, useState } from 'react';
import FlippexLoader from '../components/ui/FlippexLoader';
import { useParams, useNavigate } from 'react-router-dom';
import { api } from '../context/AuthContext';
import { ArrowLeft, ArrowRight, Buildings, ShieldCheck } from '@phosphor-icons/react';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { ActivityFeed } from '../components/investor/InvestmentCard';
import { money, pct, changeColor, shortDate, INVESTOR_BLUE, FLIPPEX_PINK } from '../components/investor/investorFormat';
import EducationSheet from '../components/instrument/EducationSheet';
import EducationCallout from '../components/instrument/EducationCallout';
import InvestmentStructureContent from '../components/instrument/InvestmentStructureContent';
import OwnershipExplainerContent from '../components/instrument/OwnershipExplainerContent';

const TABS = [
  { value: 'overview', label: 'Overview' },
  { value: 'financials', label: 'Financials' },
  { value: 'ownership', label: 'Ownership' },
  { value: 'updates', label: 'Updates' },
  { value: 'activity', label: 'Activity' },
];

const PERIODS = [
  { key: '1M', months: 1 },
  { key: '3M', months: 3 },
  { key: '1Y', months: 12 },
  { key: 'ALL', months: null },
];

/** Calm value evolution line - long-term real estate value, not a trading chart. */
const ValueChart = ({ history, period }) => {
  const cutoff = period.months
    ? new Date(new Date().setMonth(new Date().getMonth() - period.months))
    : null;
  const points = history.filter((p) => !cutoff || new Date(p.date) >= cutoff);

  if (points.length === 0) {
    return (
      <p className="text-sm text-foreground-muted py-6">
        No valuations in this period yet.
      </p>
    );
  }
  if (points.length === 1) {
    return (
      <div className="py-6">
        <p className="text-sm text-foreground-muted">
          One valuation recorded so far ({money(points[0].share_value)} per share on {shortDate(points[0].date)}). The
          value line will build up as new valuations are published.
        </p>
      </div>
    );
  }

  const values = points.map((p) => p.share_value);
  const min = Math.min(...values);
  const max = Math.max(...values);
  const span = max - min || 1;
  const coords = points.map((p, i) => {
    const x = (i / (points.length - 1)) * 100;
    const y = 100 - ((p.share_value - min) / span) * 100;
    return `${x},${y}`;
  });

  return (
    <div className="py-4" data-testid="value-chart">
      <svg viewBox="0 0 100 100" preserveAspectRatio="none" className="w-full h-28">
        <polyline points={coords.join(' ')} fill="none" stroke={INVESTOR_BLUE} strokeWidth="1.2" vectorEffect="non-scaling-stroke" />
      </svg>
      <div className="flex justify-between text-xs text-foreground-muted mt-2">
        <span>{shortDate(points[0].date)} · {money(points[0].share_value)}</span>
        <span>{shortDate(points[points.length - 1].date)} · {money(points[points.length - 1].share_value)}</span>
      </div>
    </div>
  );
};

const Panel = ({ title, action, children, testId }) => (
  <section className="bg-white rounded-2xl border border-black/[0.05]" data-testid={testId}>
    <div className="flex items-center justify-between px-5 py-4 border-b border-black/[0.05]">
      <h2 className="text-sm font-medium">{title}</h2>
      {action}
    </div>
    <div className="px-5 py-5">{children}</div>
  </section>
);

const Stat = ({ label, value, tone }) => (
  <div>
    <p className="text-[10px] uppercase tracking-[0.14em] text-foreground-muted">{label}</p>
    <p className={`text-lg font-semibold mt-1 tabular-nums ${tone || 'text-slate-900'}`}>{value}</p>
  </div>
);

export default function InvestmentDetailPage() {
  const { securityId } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState('overview');
  const [structureOpen, setStructureOpen] = useState(false);
  const [ownershipOpen, setOwnershipOpen] = useState(false);
  const [period, setPeriod] = useState(PERIODS[3]);

  useEffect(() => {
    api.get(`/portfolio/v2/investments/${securityId}`)
      .then((res) => setData(res.data))
      .catch(() => setData(null))
      .finally(() => setLoading(false));
  }, [securityId]);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-40">
        <FlippexLoader size={48} animate={false} />
      </div>
    );
  }

  if (!data) {
    return (
      <div className="container mx-auto px-5 max-w-[700px] py-24 text-center">
        <h1 className="text-xl font-heading">Investment not found</h1>
        <p className="text-sm text-foreground-muted mt-2">This investment is not part of your portfolio.</p>
        <button onClick={() => navigate('/app/portfolio')} className="mt-6 text-sm font-medium text-primary">
          Back to portfolio
        </button>
      </div>
    );
  }

  const { position, performance, instrument, ownership, project, asset, updates = [], value_history: history = [], progression, documents = [] } = data;
  const latestUpdate = updates[0];

  return (
    <>
      <div className="container mx-auto px-5 md:px-8 max-w-[1100px] py-8 md:py-12" data-testid="investment-detail-page">
        <button
          onClick={() => navigate('/app/portfolio')}
          className="inline-flex items-center gap-1.5 text-xs text-foreground-muted hover:text-slate-900"
          data-testid="back-to-portfolio"
        >
          <ArrowLeft size={13} /> Portfolio
        </button>

        {/* Header */}
        <div className="mt-4">
          <div className="flex items-center gap-2.5 flex-wrap">
            <h1 className="text-2xl md:text-3xl font-heading">{project.project_title}</h1>
            <span className="text-[11px] font-medium text-emerald-700 bg-emerald-50 rounded-md px-2 py-0.5">Active</span>
          </div>
          <p className="text-xs text-foreground-muted mt-1.5 capitalize">
            {[project.location, project.strategy].filter(Boolean).join(' · ')}
          </p>
        </div>

        <div className="mt-7">
          <p className="text-[11px] uppercase tracking-[0.16em] text-foreground-muted">Your investment</p>
          <div className="mt-2.5 flex items-end gap-3 flex-wrap">
            <p className="text-[44px] md:text-[56px] leading-none font-semibold tracking-tight tabular-nums" data-testid="investment-current-value">
              {money(performance.current_value)}
            </p>
            <p className={`text-base md:text-lg font-medium mb-1 tabular-nums ${changeColor(performance.unrealized_change)}`}>
              {money(performance.unrealized_change, { signed: true })} · {pct(position.unrealized_percent, { signed: true })}
            </p>
          </div>
          <p className="text-sm text-foreground-muted mt-2">
            Current value · {money(performance.invested)} invested
          </p>
          <p className="text-sm text-foreground-muted mt-4 tabular-nums">
            {position.units} shares · {money(performance.issue_price)} original share price · current share value{' '}
            {money(performance.share_value)}
          </p>
        </div>

        {/* Section navigation */}
        <div className="mt-7">
          <div className="sm:hidden">
            <Select value={tab} onValueChange={setTab}>
              <SelectTrigger data-testid="investment-section-selector"><SelectValue /></SelectTrigger>
              <SelectContent>
                {TABS.map((t) => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="hidden sm:flex items-center gap-1 border-b border-black/[0.06]">
            {TABS.map((t) => (
              <button
                key={t.value}
                onClick={() => setTab(t.value)}
                className={`px-3 py-2.5 text-sm border-b-2 -mb-px transition-colors ${
                  tab === t.value ? 'border-primary text-slate-900 font-medium' : 'border-transparent text-foreground-muted hover:text-slate-900'
                }`}
                data-testid={`investment-tab-${t.value}`}
              >
                {t.label}
              </button>
            ))}
          </div>
        </div>

        <div className="mt-6 space-y-4 md:space-y-5">
          {tab === 'overview' && (
            <>
              <div className="grid gap-4 md:gap-5 lg:grid-cols-2">
                <Panel title="Your position" testId="investment-position-panel">
                  <div className="grid grid-cols-2 gap-x-6 gap-y-5">
                    <Stat label="Current value" value={money(performance.current_value)} />
                    <Stat label="Amount invested" value={money(performance.invested)} />
                    <Stat
                      label="Returns received"
                      value={money(performance.income_received)}
                      tone={performance.income_received > 0 ? 'text-emerald-600' : undefined}
                    />
                    <Stat
                      label="Total economic gain"
                      value={money(performance.total_gain, { signed: performance.total_gain !== 0 })}
                      tone={changeColor(performance.total_gain)}
                    />
                  </div>
                </Panel>

                <Panel title="Underlying asset" testId="investment-asset-panel">
                  {asset ? (
                    <>
                      <div className="flex items-start gap-3">
                        <Buildings size={20} className="text-primary mt-0.5" weight="duotone" />
                        <div>
                          <p className="text-sm font-medium">{asset.name}</p>
                          <p className="text-xs text-foreground-muted mt-0.5 capitalize">
                            {[asset.location, (asset.asset_type || '').replace(/_/g, ' ')].filter(Boolean).join(' · ')}
                          </p>
                        </div>
                      </div>
                      <div className="mt-5 grid grid-cols-2 gap-6">
                        <Stat label="Current asset NAV" value={money(asset.current_nav)} />
                        <Stat label="Project strategy" value={<span className="capitalize text-base">{project.strategy || '—'}</span>} />
                      </div>
                      <button
                        onClick={() => navigate(`/app/explore/${project.project_id}`)}
                        className="mt-5 text-xs font-medium text-primary inline-flex items-center gap-1"
                        data-testid="view-property-link"
                      >
                        View property details <ArrowRight size={12} weight="bold" />
                      </button>
                    </>
                  ) : (
                    <p className="text-sm text-foreground-muted">Property details will appear here once linked.</p>
                  )}
                </Panel>
              </div>

              <Panel title="Latest update" testId="investment-update-panel">
                {latestUpdate ? (
                  <>
                    <p className="text-sm font-medium">{latestUpdate.title}</p>
                    <p className="text-xs text-foreground-muted mt-1">{shortDate(latestUpdate.date)}</p>
                    {latestUpdate.body && (
                      <p className="text-sm text-foreground-muted leading-relaxed mt-3 line-clamp-4">{latestUpdate.body}</p>
                    )}
                  </>
                ) : (
                  <p className="text-sm text-foreground-muted">
                    No project updates yet. You will see progress reports from the project team here.
                  </p>
                )}
              </Panel>

              <Panel title="Investment structure" testId="investment-structure-panel">
                <div className="flex items-center gap-2">
                  <span className="font-mono text-xs text-foreground-muted">{instrument.code}</span>
                  <span className="text-xs text-foreground-muted capitalize">
                    {(instrument.security_class || '').replace(/_/g, ' ')} · tokenized security
                  </span>
                </div>
                <div className="mt-4 grid grid-cols-2 gap-6">
                  <Stat label="Original share price" value={money(instrument.issue_price)} />
                  <Stat label="Your shares" value={position.units} />
                </div>
                <div className="mt-5">
                  <EducationCallout
                    icon={ShieldCheck}
                    title="How your investment works"
                    body="Your investment is represented by a tokenized security linked to the financial performance of the underlying property. Smart contracts record and administer the instrument on the blockchain, while a security interest over the underlying real estate provides an additional layer of investor protection. Project financial performance is independently audited."
                    ctaLabel="How your investment works"
                    onOpen={() => setStructureOpen(true)}
                    testId="investor-structure-callout"
                  />
                </div>
              </Panel>
            </>
          )}

          {tab === 'financials' && (
            <>
              <Panel title="Performance" testId="investment-performance-panel">
                <div className="grid grid-cols-2 md:grid-cols-3 gap-x-6 gap-y-6">
                  <Stat label="Original investment" value={money(performance.invested)} />
                  <Stat label="Current estimated value" value={money(performance.current_value)} />
                  <Stat
                    label="Returns received (realised)"
                    value={money(performance.income_received)}
                    tone={performance.income_received > 0 ? 'text-emerald-600' : undefined}
                  />
                  <Stat
                    label="Unrealised change"
                    value={money(performance.unrealized_change, { signed: performance.unrealized_change !== 0 })}
                    tone={changeColor(performance.unrealized_change)}
                  />
                  <Stat
                    label="Total economic gain"
                    value={money(performance.total_gain, { signed: performance.total_gain !== 0 })}
                    tone={changeColor(performance.total_gain)}
                  />
                  <Stat
                    label="Projected return (estimate)"
                    value={instrument.estimated_project_return != null ? pct(instrument.estimated_project_return) : '—'}
                  />
                </div>
                <p className="text-xs text-foreground-muted mt-5">
                  {performance.valuation_basis}
                  {performance.valuation_date ? ` · last valued ${shortDate(performance.valuation_date)}` : ''}.
                  Projected return is an estimate for the whole project and is not money already earned.
                </p>
              </Panel>

              <Panel title="Investment value" testId="investment-value-panel">
                <div className="flex items-end gap-8">
                  <Stat label="Original share value" value={money(performance.issue_price)} />
                  <Stat
                    label="Current share value"
                    value={`${money(performance.share_value)} (${pct(performance.share_value_change_percent, { signed: true })})`}
                    tone={changeColor(performance.share_value_change_percent)}
                  />
                </div>

                <div className="mt-5 flex items-center gap-1 rounded-lg bg-slate-100/80 p-0.5 w-fit">
                  {PERIODS.map((p) => (
                    <button
                      key={p.key}
                      onClick={() => setPeriod(p)}
                      className={`px-2.5 py-1 text-xs rounded-md transition-colors ${
                        period.key === p.key ? 'bg-white shadow-sm font-medium' : 'text-foreground-muted'
                      }`}
                      data-testid={`chart-period-${p.key}`}
                    >
                      {p.key}
                    </button>
                  ))}
                </div>

                <ValueChart history={history} period={period} />

                {history.length > 0 ? (
                  <div className="mt-6 divide-y divide-black/[0.05]">
                    {history.map((point, i) => (
                      <div key={i} className="py-3 flex items-baseline justify-between gap-4" data-testid="valuation-history-row">
                        <span className="text-xs text-foreground-muted w-28 flex-shrink-0">{shortDate(point.date)}</span>
                        <span className="text-sm flex-1 truncate text-foreground-muted">
                          {point.is_initial ? 'Initial valuation' : point.basis}
                        </span>
                        <span className="text-sm font-medium tabular-nums">{money(point.share_value)}</span>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-sm text-foreground-muted mt-5">
                    Valuation history will appear here after the first independent asset valuation.
                  </p>
                )}
              </Panel>

              <Panel title="Documents" testId="investment-documents-panel">
                {documents.length === 0 ? (
                  <p className="text-sm text-foreground-muted">
                    No documents available yet. Your subscription agreement, security documentation, valuations and
                    audited statements will appear here as they are published.
                  </p>
                ) : (
                  <div className="divide-y divide-black/[0.05] -my-1">
                    {documents.map((doc) => (
                      <a
                        key={doc.id}
                        href={doc.url || '#'}
                        target="_blank"
                        rel="noreferrer"
                        className="py-3 flex items-center justify-between gap-4 group"
                        data-testid={`investment-document-${doc.id}`}
                      >
                        <span className="min-w-0">
                          <span className="block text-sm group-hover:text-primary truncate">{doc.name}</span>
                          <span className="block text-xs text-foreground-muted capitalize">
                            {(doc.category || '').replace(/_/g, ' ')}{doc.date ? ` · ${shortDate(doc.date)}` : ''}
                          </span>
                        </span>
                        <ArrowRight size={13} className="text-foreground-muted flex-shrink-0" />
                      </a>
                    ))}
                  </div>
                )}
              </Panel>
            </>
          )}

          {tab === 'updates' && (
            <>
              <Panel title="Project status" testId="investment-progression-panel">
                <div className="space-y-3">
                  {(progression?.stages || []).map((stage, i) => (
                    <div key={stage.label} className="flex items-center gap-3" data-testid={`stage-${i}`}>
                      <span
                        className={`w-2.5 h-2.5 rounded-full flex-shrink-0 ${
                          stage.state === 'done' ? 'bg-emerald-500'
                            : stage.state === 'current' ? 'bg-primary ring-4 ring-primary/15'
                            : 'bg-slate-200'
                        }`}
                      />
                      <span className={`text-sm ${stage.state === 'upcoming' ? 'text-foreground-muted' : 'text-slate-900'}`}>
                        {stage.label}
                      </span>
                      {stage.state === 'current' && (
                        <span className="text-[11px] text-primary font-medium">Current stage</span>
                      )}
                    </div>
                  ))}
                </div>
              </Panel>

              <Panel title="Project timeline" testId="investment-timeline-panel">
                {updates.length === 0 ? (
                  <p className="text-sm text-foreground-muted">
                    No progress updates published yet. The project team's reports will appear here.
                  </p>
                ) : (
                  <div className="space-y-6">
                    {updates.map((u, i) => (
                      <div key={i} className="flex gap-4" data-testid="timeline-update">
                        <div className="flex flex-col items-center pt-1">
                          <span className={`w-2.5 h-2.5 rounded-full ${i === 0 ? 'bg-primary' : 'bg-slate-300'}`} />
                          {i < updates.length - 1 && <span className="flex-1 w-px bg-slate-200 mt-1" />}
                        </div>
                        <div className="pb-1 min-w-0">
                          <p className="text-xs text-foreground-muted">{shortDate(u.date)}</p>
                          <p className="text-sm font-medium mt-0.5">{u.title}</p>
                          {u.body && <p className="text-sm text-foreground-muted leading-relaxed mt-1.5">{u.body}</p>}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </Panel>
            </>
          )}

          {tab === 'ownership' && (
            <>
              <Panel
                title="Project ownership"
                testId="investment-ownership-panel"
                action={
                  <button
                    onClick={() => setOwnershipOpen(true)}
                    className="text-xs font-medium text-primary inline-flex items-center gap-1"
                    data-testid="investor-ownership-explainer-btn"
                  >
                    How ownership works <ArrowRight size={12} weight="bold" />
                  </button>
                }
              >
                <Stat label="Current asset NAV" value={money(ownership.current_nav)} />
                <div className="mt-6 grid grid-cols-2 gap-8">
                  <div>
                    <p className="text-[10px] uppercase tracking-[0.14em] text-foreground-muted">Investor ownership</p>
                    <p className="text-2xl font-semibold mt-1.5 tabular-nums" style={{ color: INVESTOR_BLUE }}>
                      {pct(ownership.investor_ownership_percent)}
                    </p>
                    <p className="text-sm text-foreground-muted mt-1 tabular-nums">{money(ownership.investor_stake_value)}</p>
                  </div>
                  <div>
                    <p className="text-[10px] uppercase tracking-[0.14em] text-foreground-muted">Flippex co-investment</p>
                    <p className="text-2xl font-semibold mt-1.5 tabular-nums" style={{ color: FLIPPEX_PINK }}>
                      {pct(ownership.flippex_ownership_percent)}
                    </p>
                    <p className="text-sm text-foreground-muted mt-1 tabular-nums">{money(ownership.flippex_stake_value)}</p>
                  </div>
                </div>
                <div className="mt-6 h-2.5 rounded-full overflow-hidden flex bg-slate-100">
                  <div className="h-full" style={{ width: `${ownership.investor_ownership_percent}%`, backgroundColor: INVESTOR_BLUE }} />
                  <div className="h-full" style={{ width: `${ownership.flippex_ownership_percent}%`, backgroundColor: FLIPPEX_PINK }} />
                </div>
              </Panel>

              <Panel title="Your position" testId="investment-your-position-panel">
                <div className="grid grid-cols-2 gap-x-6 gap-y-5">
                  <Stat label="Your shares" value={position.units} />
                  <Stat label="Original investment" value={money(performance.invested)} />
                  <Stat label="Share of the instrument" value={pct(ownership.your_share_of_instrument, { decimals: 2 })} />
                  <Stat label="Economic exposure to the asset" value={pct(ownership.your_share_of_asset, { decimals: 2 })} />
                </div>
                <p className="text-xs text-foreground-muted mt-5">
                  Your share of the instrument is your shares divided by all subscribed shares. Economic exposure is
                  that share applied to the investors' ownership of the underlying asset. You do not hold direct legal
                  title to the property.
                </p>
              </Panel>
            </>
          )}

          {tab === 'activity' && (
            <Panel title="Investment activity" testId="investment-activity-panel">
              <div className="-mx-5">
                <ActivityFeed
                  events={data.activity}
                  emptyMessage="Activity for this investment will appear here."
                />
              </div>
            </Panel>
          )}
        </div>
      </div>

      <EducationSheet
        open={structureOpen}
        onOpenChange={setStructureOpen}
        title="How your investment works"
        description="What you own and how the instrument is structured."
        testId="investor-structure-sheet"
      >
        <InvestmentStructureContent investorPercent={ownership.investor_ownership_percent} />
      </EducationSheet>

      <EducationSheet
        open={ownershipOpen}
        onOpenChange={setOwnershipOpen}
        title="How ownership works"
        description="Ownership is fixed at launch and never diluted by valuation changes."
        testId="investor-ownership-sheet"
      >
        <OwnershipExplainerContent
          currentNav={ownership.current_nav}
          totalRaise={ownership.total_raise}
          totalShares={instrument.units_subscribed}
          sharePrice={instrument.issue_price}
          investorPercent={ownership.investor_ownership_percent}
          flippexPercent={ownership.flippex_ownership_percent}
          flippexValue={ownership.flippex_stake_value}
        />
      </EducationSheet>
    </>
  );
}
